import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import { useAuth } from '~/auth';
import { Translate } from '~/i18n';
import { Arrow } from '~/components/common/arrow';
import { BusinessOnboardFilesForm } from '~/components/profile/onboard-files.section';
import { Button } from '~/components/common/button';
import {
   CheckGreenIcon,
   CheckSquareIcon,
   ClockIcon,
   ClockIconOutline,
   CloseIconGray,
   // ExternalLinkIcon,
   ItineraryIcon,
   RemoveSquareIcon,
   SpinnerAltIcon,
   TickMark,
} from '~/svgs';
import Modal from '~/components/common/modal';
import { Section } from '~/components/common/section';

type UpdateProfileTypeModalProps = {
   open: boolean;
   setOpen: (value: boolean) => void;
   refetchUser?: () => void;
};

const UpdateProfileTypeModal = ({ open, setOpen, refetchUser }: UpdateProfileTypeModalProps) => {
   const { user } = useAuth();
   // const router = useRouter();
   const [onboardingStep, setOnboardingStep] = useState<
      'welcome' | 'upload_files' | 'in_review' | undefined
   >(undefined);
   const [isNusukUser, setIsNusukUser] = useState<boolean>(false);

   useEffect(() => {
      if (user && !onboardingStep) {
         setOnboardingStep('welcome');
         setIsNusukUser(user?.is_umrah || false);
      }
   }, [onboardingStep, user]);

   const fwdBtnHandler = () => {
      setOnboardingStep('in_review');
   };

   return (
      <Modal size="lg" open={open}>
         <Section>
            <div className="flex justify-between border-b items-center px-10 py-6">
               <span className="block text-lg font-semibold">
                  <Translate id="onboard:account" />
               </span>
               <div
                  onClick={() => setOpen(false)}
                  className="flex justify-end w-full cursor-pointer">
                  <CloseIconGray />
               </div>
            </div>
            {onboardingStep === 'welcome' || onboardingStep === 'in_review' ? (
               <div className="flex justify-between items-center px-10 py-6 border-b">
                  <div className="flex flex-col gap-2">
                     <span className="">
                        <Translate id="onboard:account type" />
                     </span>
                     <span className="text-xl">
                        <Translate id="onboard:your active accounts" />
                     </span>
                     <div className="flex items-center gap-3">
                        <span className="flex items-center gap-2 text-sm text-primary">
                           <CheckSquareIcon /> <Translate id="onboard:umrah provider" />
                        </span>
                        {user?.is_umrah ? (
                           <span className="flex items-center gap-2 text-sm text-red">
                              <RemoveSquareIcon /> <Translate id="onboard:tourism provider" />
                           </span>
                        ) : (
                           <span
                              className={`flex items-center gap-2 text-sm ${
                                 user?.status !== 'Under Review' ? 'text-primary' : ''
                              }`}>
                              {user?.status === 'Under Review' ? (
                                 <ClockIcon className="[&>*]:fill-grey-200" />
                              ) : (
                                 <CheckSquareIcon />
                              )}{' '}
                              <Translate id="onboard:tourism provider" />
                           </span>
                        )}
                     </div>
                  </div>
                  {/* <Button
                     className="w-full h-10 !text-sm font-semibold gap-3 group !px-3"
                     theme="outline-primary"
                     onClick={() => {
                        console.log('Preview Legal Docs');
                     }}>
                     <ExternalLinkIcon className="w-4 h-4 group-hover:[&>*]:fill-white [&>*]:fill-primary" />
                     <span className="">
                        <Translate id="onboard:preview legal documents" />
                     </span>
                  </Button> */}
               </div>
            ) : (
               <div className="w-full px-10 py-6 border-b flex flex-col gap-2">
                  <div
                     className="flex items-center ltr:text-right rtl:text-left cursor-pointer"
                     onClick={() => setOnboardingStep('welcome')}>
                     <span className="ltr:mr-3 rtl:ml-3 ">
                        <Arrow direction="left" className="w-3 h-3" />
                     </span>{' '}
                     <span>
                        <Translate
                           id={`onboard:${user?.is_umrah ? 'tourism packages' : 'umrah packages'}`}
                        />
                     </span>
                  </div>
                  <span className="text-xl">
                     <Translate id="onboard:setup your legal documents" />
                  </span>
               </div>
            )}
            {onboardingStep === 'in_review' ? (
               <InReviewState
                  onClose={() => {
                     refetchUser?.();
                     setOpen(false);
                  }}
               />
            ) : onboardingStep === 'welcome' ? (
               <WelcomeScreen
                  setOnboardingStep={setOnboardingStep}
                  setProfileType={() => {
                     setIsNusukUser(!isNusukUser);
                  }}
               />
            ) : (
               <BusinessOnboardFilesForm
                  sectionClassName="!w-full !px-3 mx-auto !pt-0 sm:!px-10 !shadow-none"
                  formClassName="!w-full rounded-lg"
                  fwdBtnHandler={fwdBtnHandler}
                  fwdBtnTitle={<Translate id="common:continue" />}
                  footerClassName="flex justify-start bg-white"
                  isDomesticUser={true}
                  isNusukUser={isNusukUser}
                  isProfileSwitchMode
               />
            )}
         </Section>
      </Modal>
   );
};

const WelcomeScreen = ({
   setOnboardingStep,
   setProfileType,
}: {
   setOnboardingStep: (value: 'welcome' | 'upload_files' | undefined) => void;
   setProfileType: () => void;
}) => {
   const { user } = useAuth();
   return (
      <div>
         <div className="flex justify-between items-center px-10 py-8">
            <div className="w-full md:w-1/2 flex flex-col gap-2">
               <span className="">
                  <Translate
                     id={`onboard:${user?.is_umrah ? 'tourism packages' : 'umrah packages'}`}
                  />
               </span>
               <span className="text-xl">
                  <Translate
                     id={`onboard:${
                        user?.is_umrah ? 'offer tourism packages' : 'offer umrah packages only'
                     }`}
                  />
               </span>
               <p className="text-sm text-grey-300">
                  <Translate
                     id={`onboard:${
                        user?.is_umrah
                           ? 'offer tourism packages description'
                           : 'offer umrah packages description'
                     }`}
                  />
               </p>
               <div className="flex items-center gap-2 my-5">
                  <ItineraryIcon className="[&>*]:fill-primary" />
                  <span className="text-sm text-primary">
                     <Translate
                        id={`onboard:${
                           user?.is_umrah
                              ? 'create and publish tour packages'
                              : 'create and publish umrah packages'
                        }`}
                     />
                  </span>
               </div>
               <Button
                  className="w-full h-10 !text-sm font-semibold gap-3 group !px-3"
                  theme="primary"
                  onClick={() => {
                     setOnboardingStep('upload_files');
                     setProfileType();
                  }}>
                  <span>
                     <Translate id="onboard:get started" />
                  </span>
               </Button>
            </div>
            <div className="m-2">
               <Image
                  height={200}
                  width={350}
                  src="/images/profile/profile-type-toggle-banner.png"
                  alt="image"
                  quality={100}
                  objectFit="cover"
                  className=""
               />
            </div>
         </div>
      </div>
   );
};

const InReviewState = ({ onClose }: { onClose: () => void }) => {
   // const { lang } = useTranslate();
   const progressList = [
      {
         title: <Translate id="onboard:select account type" />,
         description: <Translate id="onboard:select account type description" />,
         state: 'done',
      },
      {
         title: <Translate id="onboard:upload documents" />,
         description: <Translate id="onboard:upload documents description" />,
         state: 'done',
      },
      {
         title: <Translate id="onboard:pending activation" />,
         description: <Translate id="onboard:account active description" />,
         state: 'in-progress',
      },
   ];
   return (
      <div>
         <div className="flex justify-between items-center px-10">
            <div className="w-full md:w-1/2 flex flex-col gap-4 py-8">
               <span className="bg-grey-600 w-[55px] h-[55px] flex justify-center items-center rounded border border-grey-500">
                  <SpinnerAltIcon
                     width={30}
                     height={30}
                     // className="animate-[spin_5s_linear_infinite]"
                  />
               </span>
               <span className="text-lg font-semibold">
                  <Translate id="onboard:your details are being reviewed by STA" />
               </span>
               <div className="flex items-center gap-2">
                  <ClockIconOutline width={15} className="[&>*]:fill-primary" />
                  <span className="text-sm text-primary">
                     <Translate id="onboard:create and publish tour packages" />
                  </span>
               </div>
               <p className="text-sm text-grey-300">
                  <Translate id="onboard:offer tourism packages description" />
               </p>

               <Button
                  className="!w-32 h-10 !text-sm font-semibold gap-3"
                  theme="primary"
                  onClick={() => {
                     onClose();
                  }}>
                  <span>
                     <Translate id="common:done" />
                  </span>
               </Button>
            </div>
            <div className="hidden w-1/2 lg:flex flex-col gap-3 border-l py-8 px-10">
               <div className="relative">
                  <div className="absolute border-r border-grey-300 h-2/3 mt-5 ltr:ml-4 rtl:mr-2"></div>
                  {progressList?.map((step, idx) => (
                     <div key={idx} className="flex items-center gap-4 mb-8">
                        <span className="-mt-4 z-50">
                           {step?.state !== 'done' ? (
                              <div className="w-8 h-8 rounded-full bg-gray-300 flex justify-center items-center">
                                 {step?.state === 'not-done' ? (
                                    <TickMark width={18} className="[&>g>path]:!fill-grey-200" />
                                 ) : (
                                    <SpinnerAltIcon width={18} height={18} />
                                 )}
                              </div>
                           ) : (
                              <CheckGreenIcon width={30} height={30} />
                           )}
                        </span>
                        <div className="flex flex-col">
                           <div
                              className={`my-2 text-sm font-semibold ${
                                 step?.state === 'done' ? 'text-primary' : ''
                              }`}>
                              {step?.title}
                           </div>
                           <div className="text-xs">{step?.description}</div>
                        </div>
                     </div>
                  ))}
               </div>
            </div>
         </div>
      </div>
   );
};

export default UpdateProfileTypeModal;
